import { useEffect, useState } from "react";
import useFetch from "../../hooks/useFetch";

/**
 * 判断当前用户是否为智利（CL）用户。
 *
 * 后端接口：GET /uapi/v1/pre-ipo/check-chile-user
 * - 无入参
 * - 出参 data：true 表示智利用户，false 表示非智利用户
 *
 * 仅在浏览器端通过 useEffect 自然触发请求（SSR 不会执行 useEffect）。
 * 失败时退化为非智利（isChile=false），即默认向用户展示 Pre-IPO 入口，
 * 避免接口抖动导致入口丢失。
 *
 * 说明：此处不依赖外部传入的 env 参数。Blofin nav 的渲染链路上 NavHeader
 * 并未把 env 转发给 Header，导致 store 中 env 恒为 undefined；改为直接在
 * hook 内以 useEffect + typeof window 守卫，更稳妥也更独立。
 */
const useChileCheck = (baseUrl: string) => {
  const { get } = useFetch(baseUrl);

  // undefined 表示尚未确定（请求未回），可用于做 loading/占位判断；
  // useNav 里只在 isChile === true 时隐藏，因此首帧/未登录/请求失败都不会误隐藏入口。
  const [isChile, setIsChile] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!baseUrl) return;

    let cancelled = false;
    (async () => {
      try {
        const res = (await get<{ code: number; data: boolean }>(
          `uapi/v1/pre-ipo/check-chile-user`
        )) as { code: number; data: boolean };
        if (cancelled) return;
        if (res && res.code === 200) {
          setIsChile(Boolean(res.data));
        } else {
          setIsChile(false);
        }
      } catch (error) {
        if (!cancelled) setIsChile(false);
        console.error(error);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  return { isChile };
};

export default useChileCheck;
